/**
 * Unified Course Service
 * Single source of truth for all course-related operations
 *
 * After adding a new course:
 * 1. Add entry to COURSE_BEHAVIOR below with behavior metadata
 * 2. Create lesson config file (e.g., atomichabitsConfig.json) in src/courses/
 * 3. Register in src/data/lessonsRegistry.js
 *
 * NO component changes required!
 */

import { getLessonsByCourse } from '../data/lessonsRegistry';
import { normalizeCourseId } from '../utils/courseNormalization';

// Re-export normalizeCourseId for convenience
export { normalizeCourseId };

// Enums for course behavior — use these instead of magic strings
export const PIPELINE_TYPE = Object.freeze({ NCE: 'nce', PHONICS: 'phonics', STORYBOOK: 'storybook' });
export const LAYOUT = Object.freeze({ SINGLE: 'single', TABBED: 'tabbed' });
export const CROWN_TYPE = Object.freeze({ PRACTICE: 'practice', QUIZ: 'quiz' });

// Shared vocab audio pattern - all courses use the same CDN structure
const VOCAB_AUDIO_PATTERN = 'https://videos.littlelambenglish.com/vocab_dictionary/{word}.mp3';

// Allowed 单次 assignment type keys (from assignmentTypes.json)
const ALL_SINGLE_TYPES = ['vocab', 'vocab-combined', 'sentences', 'sent-read', 'vocab-sentences', 'paragraph', 'memorize', 'vocab-sent-read'];
const NO_MEMORIZE = ALL_SINGLE_TYPES.filter(t => t !== 'memorize');

function createNceConfig(bookNumber, lessonNumberPadding) {
  return {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: true,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: true, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 3,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: ALL_SINGLE_TYPES,
    // === DISPLAY ===
    displayName: `新概念${bookNumber}`, subtitle: `New Concept English Book ${bookNumber}`,
    bookInName: true, // "新概念1" already names the book — never show "新概念1 B1"
    // Generated cover (800x450, app palette + lamb badge) in OSS course-thumbnails/ — shows on /library and the claim page.
    thumbnail: `https://videos.littlelambenglish.com/course-thumbnails/nce${bookNumber}.jpg`,
    lessonTitleFormat: 'number', freeLessons: 3, showOnCoursesPage: false,
    // Dashboard homework cards show this book cover (NCE lessons have no video frame)
    dashboardThumbs: true,
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
    sentenceAudioPattern: `https://videos.littlelambenglish.com/nce/nce${bookNumber}/lesson-{lessonNumber}/{lessonNumber}-{sentenceIndex}.mp3`,
    combinedAudioPattern: `https://videos.littlelambenglish.com/nce/nce${bookNumber}/lesson-{lessonNumber}/full.mp3`,
    lessonNumberPadding,
  };
}

/**
 * Stub course — a quiz-only entry in COURSE_BEHAVIOR with no lesson content.
 * All feature flags off, `assignable: false`, `layout: LAYOUT.SINGLE`. Used by
 * the crown/quiz lookup for courses like IPA, Sight Words, Sentences, Dolch.
 *
 * @param {{ displayName: string, subtitle: string, maxCrowns: number, themeColor: string, themeGradient: string, thumbnail?: string|null }} fields
 */
function createStubCourse({ displayName, subtitle, maxCrowns, themeColor, themeGradient, thumbnail = null }) {
  return {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: false, hasSentences: false, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: false, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.QUIZ, maxCrowns,
    layout: LAYOUT.SINGLE, itemTypes: [], assignable: false,
    // === DISPLAY ===
    displayName, subtitle, thumbnail,
    lessonTitleFormat: 'number', freeLessons: 0, showOnCoursesPage: false,
    themeColor, themeGradient,
  };
}

// ============================================================
// COURSE_BEHAVIOR — single source of truth for all course config.
// When adding a new course, copy any course below and fill in ALL behavior flags.
// The validation test in courseService.test.js will catch missing properties.
// ============================================================
const COURSE_BEHAVIOR = {
  phonics: {
    // === BEHAVIOR ===
    hasLetters: true, hasVocab: true, hasSentences: false, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: false, supportsMemorize: false,
    isLegacyCourse: true, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.QUIZ, maxCrowns: { b1: 6, default: 3 },
    layout: LAYOUT.SINGLE, itemTypes: ['letters', 'vocab'], assignable: true,
    allowedSingleTypes: ['vocab', 'vocab-combined'],
    // === DISPLAY ===
    displayName: '自然拼读', subtitle: '26个字母、初级字母组合、高级元音规则、高级辅音规则',
    lessonTitleFormat: 'letter', batches: ['b1', 'b2', 'b3', 'b4'],
    freeLessons: 3, showOnCoursesPage: true,
    // Dashboard homework cards show the lesson's video frame (videos.main.thumb)
    dashboardThumbs: true,
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/phonics.jpg',
    previewVideo: 'https://videos.littlelambenglish.com/40d86a828a3771f0804d4531858c0102/8bd01a7d946f4940bac36d364fed3b14.m3u8',
    themeColorsByBook: {
      b1: { color: '#FF914D', gradient: 'linear-gradient(135deg, #FF914D 0%, #FFB380 100%)' },
      b2: { color: '#00BF63', gradient: 'linear-gradient(135deg, #00BF63 0%, #40D98A 100%)' },
      b3: { color: '#38B6FF', gradient: 'linear-gradient(135deg, #38B6FF 0%, #6CC9FF 100%)' },
      b4: { color: '#D952FF', gradient: 'linear-gradient(135deg, #D952FF 0%, #E585FF 100%)' },
    },
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  southstreet: {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: true,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: true, hasOwnVideoRendering: true,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: true,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    displayName: '南街学校', subtitle: '《南街学校》系列动画片精讲',
    lessonTitleFormat: 'number', batches: ['b1', 'b2', 'b3'],
    // Drawer shows the 12 episodes as cover cards. The 3 books each hold 4
    // episodes; groupBy:'chapter' splits them for display only (lessons keep
    // their real book/lessonNumber). See deriveChapterSections().
    groupedDrawer: true, groupBy: 'chapter',
    freeLessons: 3, showOnCoursesPage: true,
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/southstreet.jpg',
    // Dashboard homework cards show the lesson's video frame (videos.main.thumb)
    dashboardThumbs: true,
    previewVideo: 'https://videos.littlelambenglish.com/00b0a1bb5a7371f096d96633b79f0102/d0368ee24c214613947f9c3420bcf5f3.m3u8',
    themeColorsByBook: {
      b1: { color: '#FF914D', gradient: 'linear-gradient(135deg, #FF914D 0%, #FFB380 100%)' },
      b2: { color: '#38B6FF', gradient: 'linear-gradient(135deg, #38B6FF 0%, #6CC9FF 100%)' },
      b3: { color: '#00BF63', gradient: 'linear-gradient(135deg, #00BF63 0%, #40D98A 100%)' },
    },
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  songs: {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: true, hasOwnVideoRendering: true,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    displayName: '歌曲', subtitle: '经典英文儿歌学习',
    thumbnail: 'https://videos.littlelambenglish.com/vod-8e4c4f/10bc3364b93671f080666732b68e0102/snapshots/18a6ac3388dc4d548abd3eef25a23195-00005.jpg', // Happy Birthday cover
    lessonTitleFormat: 'song', sentenceLabel: 'lyrics',
    freeLessons: 1, showOnCoursesPage: true,
    // Dashboard homework cards show the lyrics video's frame (videos.sentences.thumb):
    // one per song, livelier than the main one. Not for piggy/mkk/southstreet, whose
    // sentences video is one per book/episode (9-23 frames for 123-330 lessons).
    dashboardThumbs: 'sentences',
    themeColor: '#FF5757', themeGradient: 'linear-gradient(135deg, #FF5757 0%, #FF7B7B 100%)',
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  // Link-out "product": not an assignable course (no lessons). When a user is
  // entitled to it, /library shows a tile that navigates straight to `customRoute`
  // (the standalone song-gallery player) instead of opening a lesson drawer.
  // `assignable: false` keeps it out of all assignable-course flows; it surfaces
  // only via getCoursesForDisplayByIds()'s link-card path.
  'songs-gallery': {
    assignable: false,
    isLinkProduct: true,
    customRoute: '/songs/gallery',
    ctaLabel: '查看 →',
    // === DISPLAY ===
    displayName: '歌曲点播', subtitle: '经典英文儿歌随心听',
    thumbnail: 'https://videos.littlelambenglish.com/vod-8e4c4f/203ca9fdc7b571f0bfb85017f1e90102/snapshots/5a7f782db7ff496b8931969744f9b76d-00005.jpg', // Baby Shark cover
    freeLessons: 0, showOnCoursesPage: false,
    themeColor: '#FF5757', themeGradient: 'linear-gradient(135deg, #FF8A5C 0%, #FFB37B 100%)',
  },
  // Link-out product: the 25-week Selina英语启蒙 weekly plan (学 / 练 / 测) at /kidsSongs (was /selina until 2026-09-14; old path redirects).
  // Plan themes + content live in src/config/selinaPlanConfig.js; per-student
  // week state is served by /api/students/:id/weekly-plans/selina.
  selina: {
    assignable: false,
    isLinkProduct: true,
    customRoute: '/kidsSongs',
    // No onboarding stop after a claim (Rob, 2026-10-01: too complicated in-app).
    // The 添加到桌面 guide still lives at /kidsSongs/add and is shared in chat
    // (the WeCom welcome links it). To bring the stop back:
    //   onboardingRoute: '/kidsSongs/add',
    ctaLabel: '进入 →',
    // === DISPLAY ===
    displayName: 'Selina英语启蒙', subtitle: '每周一个主题 · 唱歌学单词',
    // Card image: the week-2 Feelings cover (This Is a Happy Face), 800x450 in OSS course-thumbnails/.
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/selina-feelings.jpg',
    freeLessons: 0, showOnCoursesPage: false,
    themeColor: '#2BB3A3', themeGradient: 'linear-gradient(135deg, #2BB3A3 0%, #5ED1C4 100%)',
  },
  // Link-out product: Selina's own Bilibili series, "Read With Selina" — 83 long-form
  // all-English episodes to watch, binge and review at /read-with-selina, each with the
  // bilingual transcript she wrote (it is burned into her videos; we recover it).
  // Deliberately NOT a course: no vocab, no crowns, nothing assignable or gradeable —
  // the whole product is "play the episode and read along". The episode list lives in
  // src/courses/readWithSelinaConfig.json, generated by
  // scripts/read-with-selina/build-config.js from that folder's episodes.json.
  readwithselina: {
    assignable: false,
    isLinkProduct: true,
    customRoute: '/read-with-selina',
    ctaLabel: '观看 →',
    // === DISPLAY ===
    displayName: 'Read With Selina', subtitle: '英文原版书精读 · 中英文稿逐句对照',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/read-with-selina.jpg',
    freeLessons: 0, showOnCoursesPage: false,
    /*
     * FREE to every signed-in user (Rob, 2026-10-17): unioned into everyone's /library 我的课程,
     * where it wears 免费 + 成人 tags, and RwsGate opens every episode (no preview, no WeChat sheet).
     * Until then it was entitlement-only (a 2026-10-04 flip to free was an accident, reverted);
     * set false to make it paid again — the preview + 小助手 sheet come back on their own.
     */
    freeToAll: true,
    // Who it's for — the 成人 tag on its /library row (kids is the unmarked default).
    audience: 'adult',
    // Bilibili blue: this product is her channel, and her audience recognises it from
    // there rather than from here. #3FC5F1 is their blue (sRGB .248 .774 .946); the
    // tile runs it into their pink #FB7299, the pairing her channel is read as.
    themeColor: '#FB7299', themeGradient: 'linear-gradient(135deg, #FB7299 0%, #3FC5F1 100%)',
  },
  piggy: {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: true, hasOwnVideoRendering: true,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // 全英问答 and 总结 are required homework from 2026-10-20 (backend
    // REQUIRED_TOPICS / SUMMARY_SINCE); 总结 drawn only with the `summary` switch.
    summaryTab: true,
    // === DISPLAY ===
    displayName: '小猪系列', subtitle: '小猪佩奇动画片精讲',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/piggy.jpg',
    lessonTitleFormat: 'number',
    groupedDrawer: true,
    // Dashboard homework cards show the lesson's video frame (videos.main.thumb)
    dashboardThumbs: true,
    batches: ['b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'b8', 'b9'],
    freeLessons: 1, showOnCoursesPage: false,
    themeColor: '#5CA8FF', themeGradient: 'linear-gradient(135deg, #5CA8FF 0%, #8FC3FF 100%)',
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  atomichabits: {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: true, hasSentences: false, hasVideoSentences: false,
    hasTranscript: true, supportsSmartReview: false, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.QUIZ, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab'], assignable: true,
    allowedSingleTypes: ['vocab', 'vocab-combined'],
    // === DISPLAY ===
    displayName: '原子习惯', subtitle: 'Atomic Habits 英文原版精讲',
    audience: 'adult', // 成人 tag on /library; 成人 row in the teacher's quick-assign picker
    lessonTitleFormat: 'chapter', transcriptLabel: '文本',
    freeLessons: 1, showOnCoursesPage: true,
    thumbnail: 'https://videos.littlelambenglish.com/vod-8e4c4f/f0066202d56471f0ac255017f0e90102/snapshots/77376b347a934ba19b1bc125bd1f4a7b-00005.jpg',
    previewVideo: 'https://videos.littlelambenglish.com/f0066202d56471f0ac255017f0e90102/5c58ba0ae1d341a2be893ef0361a9f8e.m3u8',
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  navalmanack: {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: false, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.QUIZ, maxCrowns: 6,
    layout: LAYOUT.SINGLE, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    displayName: 'Naval智慧录', subtitle: 'The Almanack of Naval Ravikant 精讲',
    audience: 'adult',
    lessonTitleFormat: 'chapter', freeLessons: 1, showOnCoursesPage: false,
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  gettingthingsdone: {
    // === BEHAVIOR === (mirror atomichabits, but no transcript)
    hasLetters: false, hasVocab: true, hasSentences: false, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: false, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.QUIZ, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab'], assignable: true,
    allowedSingleTypes: ['vocab', 'vocab-combined'],
    // === DISPLAY ===
    displayName: '搞定', subtitle: 'Getting Things Done 英文精讲',
    audience: 'adult',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/gettingthingsdone-v2.jpg',
    lessonTitleFormat: 'chapter', freeLessons: 1, showOnCoursesPage: false,
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  top10speeches: {
    // === BEHAVIOR === (mirror monkeyking)
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    // Ships five speeches, not ten. The course ID stays `top10speeches` because
    // submission rows reference it; only the display labels change.
    displayName: 'Top 5 演讲', subtitle: '五大经典英文演讲精讲',
    audience: 'adult',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/top10speeches-b3.jpg',
    lessonTitleFormat: 'number', freeLessons: 1, showOnCoursesPage: false,
    groupedDrawer: true,
    batches: ['b1', 'b2', 'b3', 'b4', 'b5'],
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  '100fans': {
    // === BEHAVIOR === (mirror top10speeches: main video + vocab + sentences per lesson)
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    displayName: '100 Fans', subtitle: '',
    audience: 'adult',
    // Card image: 800x450 JPG cover in OSS course-thumbnails/ (served resized via optimizeImageUrl).
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/100fans.jpg',
    lessonTitleFormat: 'number', freeLessons: 0, showOnCoursesPage: false,
    // Free showcase: when true, any SIGNED-IN user can open it without a paid
    // entitlement — unioned into /library for everyone and bypasses the /qr
    // enrollment gate. Scoped to this course only so it never unlocks paid
    // content. See getFreeToAllCourseIds() / isCourseFreeToAll().
    // HIDDEN for now (Rob, 2026-09-15): a kids-songs buyer saw it next to their
    // course on /library and found it confusing. Flip back to true to show it
    // to everyone again; explicit entitlements/class 24 assignments still work.
    freeToAll: false,
    // Single flat list: one book (b1) with 7 lessons (Lesson 1 → Lesson 7), so the
    // library opens straight to the lessons — no intermediate day/section drawer.
    batches: ['b1'],
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  whywesleep: {
    // === BEHAVIOR === (mirror top10speeches; tab 2 = NCE-style per-sentence audio)
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    displayName: '我们为什么睡觉', subtitle: 'Why We Sleep 精讲',
    audience: 'adult',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/whywesleep.jpg',
    // Single book of chapters (Intro + 16), like Getting Things Done.
    lessonTitleFormat: 'chapter', freeLessons: 1, showOnCoursesPage: false,
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
    sentenceAudioPattern: 'https://videos.littlelambenglish.com/whywesleep/{book}/{lessonNumber}/{sentenceIndex}.mp3',
    lessonNumberPadding: 3,
  },
  shawshank: {
    // === BEHAVIOR === (mirror top10speeches; tab 2 = NCE-style per-sentence audio)
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE,
    // === DISPLAY ===
    displayName: '肖申克的救赎', subtitle: 'The Shawshank Redemption 精讲',
    audience: 'adult',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/shawshank.jpg',
    lessonTitleFormat: 'number', freeLessons: 1, showOnCoursesPage: false,
    // Single flat list, like whywesleep: one book (b1) of 30 clips (片头介绍 +
    // 场景 1-1 … 10-2), so the drawer opens straight to the lessons. The scene
    // grouping lives in the titles, not in an intermediate level-1 drawer — the
    // 10-book grouped drawer this replaced was an empty placeholder skeleton.
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
    sentenceAudioPattern: 'https://videos.littlelambenglish.com/shawshank/{book}/{lessonNumber}/{sentenceIndex}.mp3',
    lessonNumberPadding: 3,
  },
  nce1: createNceConfig(1, 3),
  nce2: createNceConfig(2, 2),
  nce3: createNceConfig(3, 2),
  nce4: createNceConfig(4, 2),
  classicchinesestories: {
    // === BEHAVIOR ===
    hasLetters: false, hasVocab: false, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: true, bilingualTitle: true,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.QUIZ, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['sentences'], assignable: true,
    allowedSingleTypes: ['sentences', 'paragraph', 'sent-read'],
    // === DISPLAY ===
    displayName: '经典故事', subtitle: '中国经典故事英文精讲',
    lessonTitleFormat: 'number', freeLessons: 1, showOnCoursesPage: false,
    // === AUDIO ===
    sentenceAudioPattern: 'https://videos.littlelambenglish.com/classicChineseStories/{lessonNumber}/{sentenceIndex}.mp3',
    lessonNumberPadding: 0,
  },
  monkeyking: {
    // === BEHAVIOR ===
    // summaryTab: a 4th tab, 总结 — retell the story in one recording. Drawn
    // only for students with the `summary` switch (see QRPage).
    summaryTab: true,
    hasLetters: false, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: true, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.TABBED, itemTypes: ['vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE, batches: ['b1', 'b2'],
    // === DISPLAY ===
    displayName: '西游记', subtitle: 'Real Chinese Monkey King 精讲',
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking.jpg',
    lessonTitleFormat: 'number', freeLessons: 1, showOnCoursesPage: false,
    // The drawer lists episodes (split by sentence video, like southstreet) — cleaner
    // for a child studying alone than five 50-80-lesson books. Books 1-5 hold 23 Little
    // Fox "Journey to the West" episodes; names in BOOK_NAMES (monkeyking|ep1…ep23).
    groupedDrawer: true, groupBy: 'chapter',
    // Dashboard homework cards show the lesson's video frame (videos.main.thumb)
    dashboardThumbs: true,
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
  ipa: createStubCourse({ displayName: '国际音标', subtitle: '掌握45个国际音标', maxCrowns: 6, themeColor: '#38B6FF', themeGradient: 'linear-gradient(135deg, #38B6FF 0%, #1A7FCC 100%)', thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/ipa.jpg' }),
  'sight-words': createStubCourse({ displayName: '高频词', subtitle: '300个最常见英语单词', maxCrowns: 3, themeColor: '#00BF63', themeGradient: 'linear-gradient(135deg, #00BF63 0%, #008F4A 100%)', thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/sight-words.jpg' }),
  sentences: createStubCourse({ displayName: '句型', subtitle: '从简单句到日常对话', maxCrowns: 6, themeColor: '#D952FF', themeGradient: 'linear-gradient(135deg, #D952FF 0%, #A020CC 100%)' }),
  dolch: createStubCourse({ displayName: '道奇视觉词220个', subtitle: 'Dolch Sight Words — 220个最常用英语单词', maxCrowns: 3, themeColor: '#FF6B6B', themeGradient: 'linear-gradient(135deg, #FF6B6B 0%, #CC4444 100%)' }),
  // maxCrowns is lessonNumber-keyed (see getMaxCrowns) since "categories" packs 27
  // distinct nodes — 20 topics + 7 shared skills — into one course/book, each with its
  // own depth. Must match backend/utils/courseBehavior.js's COURSE_CONFIG.categories
  // and categoriesBuilder.js's CROWN_BUILDERS array length per node — no test enforces
  // this agreement, bump all three together.
  categories: createStubCourse({
    displayName: '主题词汇', subtitle: '家庭、活动、国家 — 分级词汇与句型',
    maxCrowns: {
      b1: {
        // Topics (5 levels each)
        '001': 5, // Family
        '002': 5, // Activities
        '003': 5, // Countries
        '004': 5, // School
        '005': 5, // Food & Meals
        '006': 5, // Animals & Pets
        '007': 5, // Daily Routine & Time
        '008': 5, // Weather & Seasons
        '009': 5, // Emotions & Feelings
        '010': 5, // Colors & Shapes
        '011': 5, // Clothes
        '012': 5, // Home & Rooms
        '013': 5, // Body & Health
        '014': 5, // Transportation
        '015': 5, // Places in Town
        '016': 5, // Toys & Games
        '017': 5, // Jobs & Work
        '018': 5, // Birthdays & Celebrations
        '019': 5, // Nature & Outdoors
        '020': 5, // Greetings & Politeness
        '021': 5, // Shopping & Money
        '022': 5, // Sports & Exercise
        // Shared skills (variable depth)
        '101': 3, // Numbers & Counting
        '102': 2, // Descriptive Adjectives
        '103': 4, // Asking Questions
        '104': 2, // Comparing Things
        '105': 3, // Talking About the Past
        '106': 2, // Connecting Ideas
        '107': 1, // Frequency & Habits
        '108': 2, // Modals & the Future
        '109': 2, // Pronouns & Possessives
        '110': 2, // Everyday Actions
        // Middle school (初中) topics
        '201': 5, // Society & Community
        '202': 5, // Environment & Our Planet
        '203': 5, // Culture & History
        '204': 5, // Technology & Media
        default: 5,
      },
    },
    themeColor: '#FF9F40', themeGradient: 'linear-gradient(135deg, #FF9F40 0%, #CC6F1A 100%)',
  }),
  // Curated /library bundle — a real course whose lessons are composed at load time
  // from phonics b1/b2 (letters+vocab) + songs (vocab+lyrics) into ONE flat book (b1,
  // renumbered 001-081); see lessonsRegistry. Rendered as a single merged list (flat
  // drawer, no per-book headers). The two content styles need different render layouts,
  // so each composed lesson carries a per-lesson `layout` ('single'/'tabbed') that
  // VocabStep prefers over this course default. isLegacyCourse:false routes phonics
  // lessons through the generic-single branch (variant="phonics", letters); songs
  // sentences carry videoStart so they keep video under the tabbed branch. Gated on
  // /library via a product_courses row.
  level1zero: {
    // === BEHAVIOR ===
    hasLetters: true, hasVocab: true, hasSentences: true, hasVideoSentences: false,
    hasTranscript: false, supportsSmartReview: false, supportsMemorize: false,
    isLegacyCourse: false, hasOwnVideoRendering: false,
    injectLessonTitle: false, bilingualTitle: false,
    separateVocabSentenceCompletion: false,
    crownType: CROWN_TYPE.PRACTICE, maxCrowns: 6,
    layout: LAYOUT.SINGLE, // default; per-lesson `layout` overrides in VocabStep
    itemTypes: ['letters', 'vocab', 'sentences'], assignable: true,
    allowedSingleTypes: NO_MEMORIZE, batches: ['b1'],
    // === DISPLAY ===
    displayName: '一级 · 零基础', subtitle: '零基础启蒙：自然拼读 + 英文儿歌',
    lessonTitleFormat: 'number', sentenceLabel: 'lyrics',
    groupedDrawer: false, showOnCoursesPage: false, freeLessons: 0,
    thumbnail: 'https://videos.littlelambenglish.com/course-thumbnails/phonics.jpg',
    themeColor: '#FF914D', themeGradient: 'linear-gradient(135deg, #FF914D 0%, #FFB380 100%)',
    // === AUDIO ===
    vocabAudioPattern: VOCAB_AUDIO_PATTERN,
  },
};

/**
 * Get course config by ID (handles aliases)
 * Returns behavior config with additional computed fields for compatibility
 */
export function getCourse(courseId) {
  if (!courseId) return null;
  const normalized = normalizeCourseId(courseId);
  const behavior = COURSE_BEHAVIOR[normalized];
  if (!behavior) return null;

  // Return a compatible shape for any legacy usages
  return {
    id: normalized,
    title: behavior.displayName,
    subtitle: behavior.subtitle || '',
    image: behavior.thumbnail || null,
    previewVideo: behavior.previewVideo || null,
    ...behavior,
  };
}

/**
 * Get course behavior metadata
 */
export function getCourseBehaviorConfig(courseId) {
  if (!courseId) return null;
  const normalized = normalizeCourseId(courseId);
  return COURSE_BEHAVIOR[normalized] || null;
}

/**
 * Get specific course behavior property with default value
 */
export function getCourseBehavior(courseId, property, defaultValue = false) {
  const behavior = getCourseBehaviorConfig(courseId);
  return behavior?.[property] ?? defaultValue;
}

/**
 * Check if course has a specific feature
 */
export function courseHas(courseId, feature) {
  return getCourseBehavior(courseId, feature, false);
}

/**
 * Get the pipeline type for a course.
 * - NCE courses: 'nce' (full 18-position pipeline)
 * - Phonics: 'phonics' (review-only pipeline D1→D60)
 * - Everything else: 'storybook' (3-tab: inclass/online/single)
 */
export function getPipelineType(courseId) {
  if (/^nce\d$/.test(courseId)) return PIPELINE_TYPE.NCE;
  if (courseId === 'phonics') return PIPELINE_TYPE.PHONICS;
  const behavior = COURSE_BEHAVIOR[courseId];
  if (!behavior?.assignable) return PIPELINE_TYPE.NCE;
  return PIPELINE_TYPE.STORYBOOK;
}

/**
 * Get courses for QuickAssignModal
 * Returns flat array of {id, label, topic, batch, pipelineType, audience} for each assignable course+batch
 */
export function getQuickAssignCourses() {
  const courses = [];
  for (const [courseId, behavior] of Object.entries(COURSE_BEHAVIOR)) {
    if (!behavior.assignable) continue;
    const batches = behavior.batches || ['b1'];
    const pipelineType = getPipelineType(courseId);
    for (const batch of batches) {
      const batchNum = batch.replace(/\D/g, '');
      courses.push({
        id: batches.length > 1 ? `${courseId}-${batch}` : courseId,
        label: batches.length > 1
          ? `${behavior.displayName} ${batchNum}`
          : behavior.displayName,
        topic: courseId,
        batch,
        pipelineType,
        audience: behavior.audience === 'adult' ? 'adult' : 'kids',
      });
    }
  }
  return courses;
}

/**
 * Get display name for course
 */
export function getCourseDisplayName(courseId) {
  const behavior = getCourseBehaviorConfig(courseId);
  if (behavior?.displayName) return behavior.displayName;
  // Fallback to capitalized courseId if no behavior config exists
  if (!courseId) return 'Unknown';
  return courseId.charAt(0).toUpperCase() + courseId.slice(1);
}

/**
 * Course plus book for one-line labels ("小猪佩奇 B3"). Courses whose name already
 * says the book (NCE: "新概念1") show the name alone.
 */
/**
 * Course plus book in words ("西游记 第2册") for the student's map and search.
 * NCE's name already says the book, so it is "新概念1", never "新概念1 第1册".
 */
export function getCourseBookTitle(courseId, batch) {
  const name = getCourseDisplayName(courseId);
  if (getCourseBehaviorConfig(courseId)?.bookInName) return name;
  return `${name} 第${batch?.replace(/[^\d]/g, '') || '1'}册`;
}

export function getCourseBookLabel(courseId, batch) {
  const name = getCourseDisplayName(courseId);
  if (!batch || getCourseBehaviorConfig(courseId)?.bookInName) return name;
  return `${name} ${batch.toUpperCase()}`;
}

/**
 * Get all valid course IDs. Excludes link-out "products" (e.g. songs-gallery)
 * which aren't real courses — they have no lessons and must not appear in
 * course/topic pickers (teacher assignment filters, getAllTopics).
 */
export function getAllCourseIds() {
  return Object.keys(COURSE_BEHAVIOR).filter(id => !COURSE_BEHAVIOR[id].isLinkProduct);
}

/**
 * Check if a course ID is valid
 */
export function isValidCourse(courseId) {
  if (!courseId) return false;
  const normalized = normalizeCourseId(courseId);
  return normalized in COURSE_BEHAVIOR;
}

/** Whether a course supports smart review (teacher word-level feedback) */
export function supportsSmartReview(courseId) {
  return getCourseBehavior(courseId, 'supportsSmartReview', true);
}

/** Whether a course injects lesson title as first sentence item */
export function shouldInjectTitle(courseId) {
  return getCourseBehavior(courseId, 'injectLessonTitle', false);
}

/** Whether a course uses practice-based crowns vs quiz-based crowns */
export function isPracticeCrown(courseId) {
  return getCourseBehavior(courseId, 'crownType', CROWN_TYPE.QUIZ) === CROWN_TYPE.PRACTICE;
}

/** Whether a course is a legacy course (hardcoded lesson objects, not generic pages) */
export function isLegacyCourse(courseId) {
  return getCourseBehavior(courseId, 'isLegacyCourse', false);
}

/**
 * Get max crowns for a course+batch, optionally overridden per lessonNumber.
 * `maxCrowns` is normally a plain number or a batch-keyed object of numbers
 * (e.g. phonics: `{b1:6, default:3}`). A course whose lessons need different
 * depths (categories: each "lesson" is a distinct skill/topic node) can nest
 * a lessonNumber-keyed object one level deeper instead of a plain number —
 * e.g. `{b1: {'001':3, '002':5, default:5}}` — resolved only when a
 * lessonNumber is passed in, so every existing batch-keyed course is unaffected.
 */
export function getMaxCrowns(courseId, batch, lessonNumber) {
  const val = getCourseBehavior(courseId, 'maxCrowns', 6);
  if (typeof val !== 'object' || val === null) return val;
  const batchVal = val[batch] ?? val.default ?? 6;
  if (typeof batchVal === 'object' && batchVal !== null) {
    return (lessonNumber && batchVal[lessonNumber]) ?? batchVal.default ?? 6;
  }
  return batchVal;
}

/**
 * Get topic label map for exports/display
 */
const BOOK_NAMES = {
  'phonics|b1': '26个字母',
  'phonics|b2': '初级字母组合',
  'phonics|b3': '高级元音规则',
  'phonics|b4': '高级辅音规则',
  'monkeyking|b1': '第一册',
  'monkeyking|b2': '第二册',
  'monkeyking|b3': '第三册',
  'monkeyking|b4': '第四册',
  'monkeyking|b5': '第五册',
  // Monkey King episodes, in course order. Titles are Little Fox's own ("Journey to the West
  // N: Title"; N in the trailing comment); each was matched to its section by text
  // (littlefox_scripts/monkeyKing/scraped_content.txt), 2026-10-12.
  'monkeyking|ep1': '第1集 The Monkey', // LF 1
  'monkeyking|ep2': '第2集 The Waterfall Cave', // LF 2
  'monkeyking|ep3': '第3集 Subodhi', // LF 3
  'monkeyking|ep4': '第4集 The Powerful Sage', // LF 10
  'monkeyking|ep5': '第5集 Trouble in Heaven', // LF 11
  'monkeyking|ep6': '第6集 The Bet', // LF 12
  'monkeyking|ep7': '第7集 The True Scriptures', // LF 13
  'monkeyking|ep8': '第8集 A Promise to Protect', // LF 14
  'monkeyking|ep9': '第9集 Tea with a Dragon', // LF 15
  'monkeyking|ep10': '第10集 Wukong Gets Tricked', // LF 16
  'monkeyking|ep11': "第11集 Guanyin's Vase", // LF 31
  'monkeyking|ep12': '第12集 Lady Whitebone', // LF 32
  'monkeyking|ep13': '第13集 The Scared Boy', // LF 48
  'monkeyking|ep14': '第14集 Red Boy', // LF 49
  'monkeyking|ep15': '第15集 Magic Fire', // LF 50
  'monkeyking|ep16': '第16集 Guanyin Becomes Angry', // LF 51
  'monkeyking|ep17': '第17集 An Ocean from a Vase', // LF 52
  'monkeyking|ep18': '第18集 The Man in the Canoe', // LF 53
  'monkeyking|ep19': '第19集 Money', // LF 71
  'monkeyking|ep20': '第20集 A Surprise at the Fruit and Flower Mountain', // LF 72
  'monkeyking|ep21': '第21集 Two Wukongs', // LF 73
  'monkeyking|ep22': '第22集 Curious Ear', // LF 74
  'monkeyking|ep23': '第23集 Battle of the Wukongs', // LF 75
  'piggy|b1': 'I Love My New Toy!《我超爱我的新玩具!》',
  'piggy|b2': 'I Am Going!《我要走了!》',
  'piggy|b3': 'I Really Like Slop!《我真的很喜欢泔水!》',
  'piggy|b4': 'Can I Play Too?《我能一起玩吗?》',
  'piggy|b5': 'Are You Ready To Play Outside?《你准备好出去玩了吗?》',
  'piggy|b6': 'Should I Share My Ice Cream?《我该分享我的冰激凌吗?》',
  'piggy|b7': 'Waiting Is Not Easy!《等待可真不容易!》',
  'piggy|b8': 'My New Friend Is So Fun!《我的新朋友真有趣!》',
  'piggy|b9': 'There Is a Bird on Your Head!《你头上有一只鸟!》',
  'nce|nce1': '新概念1',
  'nce|nce2': '新概念2',
  'nce|nce3': '新概念3',
  'nce|nce4': '新概念4',
  // The course ships FIVE speeches, not the original ten-speech plan. b1 and b3 were
  // already live (b1 carries submissions), so the new ones filled b2/b4/b5 rather than
  // renumbering anything. The unrecorded speeches (Obama, Cuddy, Treasure, Churchill,
  // Vanderkam, Suzuki) and their empty 45-lesson scaffolds are gone.
  'top10speeches|b1': 'Steve Jobs - Stanford Commencement',
  'top10speeches|b2': 'Simon Sinek - How Great Leaders Inspire Action',
  'top10speeches|b3': 'Martin Luther King - I Have a Dream',
  'top10speeches|b4': 'Jeff Bezos - What Matters More Than Your Talents',
  'top10speeches|b5': 'J.K. Rowling - The Fringe Benefits of Failure',
  // (100fans is a single flat book b1 with 7 lessons — no per-book label needed)
  // southstreet episodes — keyed by chapter id (ep1…ep12), see deriveChapterSections
  'southstreet|ep1': 'Episode 1',
  'southstreet|ep2': 'Episode 2',
  'southstreet|ep3': 'Episode 3',
  'southstreet|ep4': 'Episode 4',
  'southstreet|ep5': 'Episode 5',
  'southstreet|ep6': 'Episode 6',
  'southstreet|ep7': 'Episode 7',
  'southstreet|ep8': 'Episode 8',
  'southstreet|ep9': 'Episode 9',
  'southstreet|ep10': 'Episode 10',
  'southstreet|ep11': 'Episode 11',
  'southstreet|ep12': 'Episode 12',
};

export function getBookName(topic, batch) {
  if (BOOK_NAMES[`${topic}|${batch}`]) return BOOK_NAMES[`${topic}|${batch}`];
  // NCE's name is the book ("新概念1"), so "Book 1" would say it twice. In the
  // merged NCE 1-4 picker the "batch" is the course itself (nce2).
  if (getCourseBehaviorConfig(topic)?.bookInName) return getCourseDisplayName(topic);
  if (getCourseBehaviorConfig(batch)?.bookInName) return getCourseDisplayName(batch);
  return `Book ${batch?.replace(/\D/g, '')}`;
}

// Per-book thumbnail URLs (used by the grouped drawer's level-1 cards, e.g. the 10
// speeches). Courses/books without an entry return null → gradient placeholder.
const BOOK_THUMBS = {
  // Getting Things Done has no per-lesson video thumbs — reuse the product cover as the
  // chapter cover so lesson rows show the cover + number overlay (like the Top 10 Speeches lessons).
  'gettingthingsdone|b1': 'https://videos.littlelambenglish.com/course-thumbnails/gettingthingsdone-v2.jpg',
  // Why We Sleep — same: reuse the product cover as the chapter cover.
  'whywesleep|b1': 'https://videos.littlelambenglish.com/course-thumbnails/whywesleep.jpg',
  'top10speeches|b1': 'https://videos.littlelambenglish.com/course-thumbnails/top10speeches-b1.jpg',
  'top10speeches|b2': 'https://videos.littlelambenglish.com/course-thumbnails/top10speeches-b2.jpg',
  'top10speeches|b3': 'https://videos.littlelambenglish.com/course-thumbnails/top10speeches-b3.jpg',
  'top10speeches|b4': 'https://videos.littlelambenglish.com/course-thumbnails/top10speeches-b4.jpg',
  'top10speeches|b5': 'https://videos.littlelambenglish.com/course-thumbnails/top10speeches-b5.jpg',
  'piggy|b1': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b1.jpg',
  'piggy|b2': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b2.jpg',
  'piggy|b3': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b3.jpg',
  'piggy|b4': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b4.jpg',
  'piggy|b5': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b5.jpg',
  'piggy|b6': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b6.jpg',
  'piggy|b7': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b7.jpg',
  'piggy|b8': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b8.jpg',
  'piggy|b9': 'https://videos.littlelambenglish.com/course-thumbnails/piggy-b9.jpg',
  // southstreet episode covers — keyed by chapter id (ep1…ep12), see deriveChapterSections
  'southstreet|ep1': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b1.jpg',
  'southstreet|ep2': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b2.jpg',
  'southstreet|ep3': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b3.jpg',
  'southstreet|ep4': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b4.jpg',
  'southstreet|ep5': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b5.jpg',
  'southstreet|ep6': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b6.jpg',
  'southstreet|ep7': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b7.jpg',
  'southstreet|ep8': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b8.jpg',
  'southstreet|ep9': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b9.jpg',
  'southstreet|ep10': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b10.jpg',
  'southstreet|ep11': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b11.jpg',
  'southstreet|ep12': 'https://videos.littlelambenglish.com/course-thumbnails/southstreet-b12.jpg',
  // monkeyking episode covers — keyed by chapter id (ep1…ep6), see deriveChapterSections
  'monkeyking|ep1': 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking-ep1.jpg',
  'monkeyking|ep2': 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking-ep2.jpg',
  'monkeyking|ep3': 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking-ep3.jpg',
  'monkeyking|ep4': 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking-ep4.jpg',
  'monkeyking|ep5': 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking-ep5.jpg',
  'monkeyking|ep6': 'https://videos.littlelambenglish.com/course-thumbnails/monkeyking-ep6.jpg',
};

export function getBookThumb(topic, batch) {
  return BOOK_THUMBS[`${topic}|${batch}`] || null;
}

export function getTopicLabelMap() {
  const map = {};
  for (const [courseId, behavior] of Object.entries(COURSE_BEHAVIOR)) {
    map[courseId] = behavior.displayName;
  }
  return map;
}

/**
 * Default drawer sections: one section per book, numeric order so b10 sorts
 * after b9 (not after b1).
 */
function buildBookSections(courseId, lessons) {
  const bookGroups = {};
  lessons.forEach(lesson => {
    const book = lesson.book || 'b1';
    if (!bookGroups[book]) bookGroups[book] = [];
    bookGroups[book].push(lesson);
  });
  return Object.entries(bookGroups)
    .sort(([a], [b]) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10))
    .map(([book, bookLessons]) => ({
      id: `${courseId}-${book}`,
      bookNumber: book,
      title: `第${book.replace(/\D/g, '')}册`,
      lessonCount: bookLessons.length,
      lessons: bookLessons,
    }));
}

/**
 * Split a course's lessons into per-episode "chapter" sections for the grouped
 * drawer (groupBy:'chapter'). Lessons are stored under a few coarse books (e.g.
 * southstreet b1 = episodes 1-4) but each book contains several episodes; an
 * episode boundary is where the per-episode `videos.sentences.hls` changes to a
 * different video. Vocab-only lessons (no sentences video) inherit the current
 * episode, and every book starts a fresh chapter.
 *
 * Display-only: each lesson keeps its real book/lessonNumber so student progress
 * and /qr/ URLs are unaffected. Chapter ids are `ep1`…`epN` — a namespace
 * distinct from the real books (b1/b2/b3), so getBookName/getBookThumb lookups
 * for the real books stay on their fallback.
 */
function deriveChapterSections(courseId, lessons) {
  const sentenceVideoId = (lesson) => {
    const hls = lesson?.videos?.sentences?.hls || '';
    const m = hls.match(/\/([0-9a-f]{20,})\//);
    return m ? m[1] : null;
  };

  const byBook = {};
  lessons.forEach(lesson => {
    const book = lesson.book || 'b1';
    if (!byBook[book]) byBook[book] = [];
    byBook[book].push(lesson);
  });
  const books = Object.keys(byBook).sort(
    (a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10)
  );

  const chapters = [];
  books.forEach(book => {
    const bookLessons = byBook[book].slice().sort((a, b) => (a.seq || 0) - (b.seq || 0));
    let current = null;       // lessons of the chapter being filled
    let currentVid = null;    // episode video id of the current chapter
    bookLessons.forEach(lesson => {
      const vid = sentenceVideoId(lesson);
      if (!current || (vid && vid !== currentVid)) {
        current = [];
        chapters.push(current);
      }
      if (vid) currentVid = vid;
      current.push(lesson);
    });
  });

  return chapters.map((chapterLessons, idx) => {
    const chapterId = `ep${idx + 1}`;
    return {
      id: `${courseId}-${chapterId}`,
      bookNumber: chapterId,
      title: getBookName(courseId, chapterId),
      lessonCount: chapterLessons.length,
      lessons: chapterLessons,
    };
  });
}

/**
 * Build the display card object for a single course id.
 * Shared by getCoursesForDisplay (public) and getCoursesForDisplayByIds (entitled).
 * Returns null if the course is unknown or has no lesson content (e.g. stub courses).
 * @param {string} courseId
 * @returns {Promise<Object|null>}
 */
async function buildCourseDisplayCard(courseId) {
  const behavior = COURSE_BEHAVIOR[courseId];
  if (!behavior) return null;

  // Get lessons to calculate count and group by book
  const lessons = await getLessonsByCourse(courseId);
  if (!lessons || lessons.length === 0) return null;

  // Build drawer sections: one per book by default, or one per episode for
  // groupBy:'chapter' courses (southstreet) — display-only, see helpers above.
  const sections = behavior.groupBy === 'chapter'
    ? deriveChapterSections(courseId, lessons)
    : buildBookSections(courseId, lessons);

  return {
    id: courseId,
    title: behavior.displayName,
    subtitle: behavior.subtitle || '',
    thumbnail: behavior.thumbnail || null,
    previewVideo: behavior.previewVideo || null,
    lessonCount: lessons.length,
    freeLessons: behavior.freeLessons || 0,
    groupedDrawer: behavior.groupedDrawer || false,
    sections,
    lessonTitleFormat: behavior.lessonTitleFormat,
  };
}

/**
 * Get courses for display on the public courses page
 * Returns only courses with showOnCoursesPage: true
 * Includes lesson count derived from lesson configs
 */
export async function getCoursesForDisplay() {
  const ids = Object.keys(COURSE_BEHAVIOR).filter(id => COURSE_BEHAVIOR[id].showOnCoursesPage);
  const cards = await Promise.all(ids.map(buildCourseDisplayCard));
  return cards.filter(Boolean);
}

/**
 * Ids of the public, browsable courses (showOnCoursesPage: true), openable at
 * /courses. Synchronous — the single source of truth for "which courses can we
 * surface to anonymous visitors" (e.g. the /free learning-plan funnel).
 * @returns {string[]}
 */
export function getPublicCourseIds() {
  return Object.keys(COURSE_BEHAVIOR).filter(id => COURSE_BEHAVIOR[id].showOnCoursesPage);
}

/**
 * Ids of "free to all" courses — openable by ANY signed-in user with no paid
 * entitlement (free showcases/funnels). Unioned into the library for every user
 * and used to bypass the /qr enrollment gate. Distinct from getPublicCourseIds()
 * (anonymous /courses browsing): freeToAll still requires login. Synchronous.
 * @returns {string[]}
 */
export function getFreeToAllCourseIds() {
  return Object.keys(COURSE_BEHAVIOR).filter(id => COURSE_BEHAVIOR[id].freeToAll);
}

/**
 * True if a course is free to any signed-in user (no entitlement needed).
 * @param {string} courseId
 * @returns {boolean}
 */
export function isCourseFreeToAll(courseId) {
  return !!COURSE_BEHAVIOR[normalizeCourseId(courseId)]?.freeToAll;
}

/**
 * Get courses for display from an explicit course-id list (e.g. a user's entitled
 * courses), ignoring showOnCoursesPage. Normalizes + de-dupes ids; drops
 * non-assignable stub/quiz courses (ipa, sight-words, sentences, dolch — not built
 * for the /qr/ lesson viewer) and courses with no lesson content. Returns the same
 * card shape as getCoursesForDisplay().
 * @param {string[]} courseIds
 * @returns {Promise<Array>}
 */
export async function getCoursesForDisplayByIds(courseIds) {
  const ids = [...new Set((courseIds || []).map(normalizeCourseId).filter(Boolean))]
    .filter(id => COURSE_BEHAVIOR[id]);
  // Link-out products (e.g. songs-gallery) have no lessons — they render a tile
  // that navigates straight to behavior.customRoute. Build their cards directly,
  // bypassing the assignable filter and the no-lesson drop below.
  const linkCards = ids
    .filter(id => COURSE_BEHAVIOR[id].customRoute)
    .map(buildLinkProductCard);
  const courseCards = await Promise.all(
    ids
      .filter(id => !COURSE_BEHAVIOR[id].customRoute && COURSE_BEHAVIOR[id].assignable !== false)
      .map(buildCourseDisplayCard)
  );
  return [...courseCards.filter(Boolean), ...linkCards];
}

/**
 * Build a display card for a link-out product (no lessons). The tile navigates to
 * `customRoute` instead of opening a lesson drawer. See COURSE_BEHAVIOR entries
 * with `isLinkProduct: true` (e.g. songs-gallery → /songs/gallery).
 * @param {string} courseId
 */
function buildLinkProductCard(courseId) {
  const behavior = COURSE_BEHAVIOR[courseId];
  return {
    id: courseId,
    title: behavior.displayName,
    subtitle: behavior.subtitle || '',
    thumbnail: behavior.thumbnail || null,
    customRoute: behavior.customRoute,
    ctaLabel: behavior.ctaLabel || '查看 →',
    isLink: true,
  };
}

/**
 * Get lessons for a course from the registry (async - lazy loads config)
 */
export async function getLessonsForCourse(courseId) {
  return getLessonsByCourse(courseId);
}

/**
 * Find a specific lesson (async - lazy loads config)
 */
export async function findLessonInCourse(courseId, book, lessonNumber) {
  const lessons = await getLessonsForCourse(courseId);
  const normalizedLessonNum = String(lessonNumber).padStart(3, '0');

  return lessons.find(
    l => l.book === book && l.lessonNumber === normalizedLessonNum
  ) || lessons.find(
    l => l.book === book && parseInt(l.lessonNumber) === parseInt(lessonNumber)
  ) || null;
}

/**
 * Format assignment title based on course type
 * Replaces scattered formatAssignmentTitle functions in components
 */
export async function formatAssignmentTitle(courseId, batch, lessonNumber) {
  const behavior = getCourseBehaviorConfig(courseId);
  if (!behavior) return `Lesson ${lessonNumber}`;

  const format = behavior.lessonTitleFormat || 'number';

  switch (format) {
    case 'letter': {
      // Phonics: Use actual letter from lesson title config
      const { getLessonLabelCache } = await import('../utils/lessonTitle');
      const cache = await getLessonLabelCache();
      const batchKey = batch?.toLowerCase().startsWith('b') ? batch.toLowerCase() : `b${batch?.replace(/\D/g, '') || '1'}`;
      const letter = cache[`phonics|${batchKey}|${parseInt(lessonNumber)}`];
      const bookNumber = batch?.replace(/\D/g, '') || '1';
      return letter
        ? `Phonics ${bookNumber}, 字母${letter.charAt(0).toUpperCase()}`
        : `Phonics ${bookNumber}, 第${lessonNumber}课`;
    }
    case 'song': {
      // Songs: Use song name from lesson title config
      const { getLessonLabelCache } = await import('../utils/lessonTitle');
      const cache = await getLessonLabelCache();
      const batchKey = batch?.toLowerCase().startsWith('b') ? batch.toLowerCase() : `b${batch?.replace(/\D/g, '') || '1'}`;
      const songName = cache[`songs|${batchKey}|${parseInt(lessonNumber)}`];
      return songName ? `Songs: ${songName}` : `歌曲 ${lessonNumber}`;
    }
    case 'chapter': {
      // Chapter-based courses: "Chapter 1", "第1章"
      return `第${parseInt(lessonNumber)}章`;
    }
    case 'number':
    default:
      return `第${parseInt(lessonNumber)}课`;
  }
}

/**
 * Get required submission count for a lesson
 * Replaces the 40-line getRequiredSubmissions function in ExportModal
 */
export async function getRequiredSubmissions(courseId, batch, lessonNumber) {
  const lesson = await findLessonInCourse(courseId, batch, lessonNumber);
  if (!lesson?.pages) return 0;

  const behavior = getCourseBehaviorConfig(courseId);
  if (!behavior) return 0;

  let count = 0;

  // Count items based on course's item types
  Object.values(lesson.pages).forEach(page => {
    if (!page.sections) return;

    // Count letters if course has them
    if (behavior.hasLetters && page.sections.letters) {
      count += page.sections.letters.length;
    }

    // Count vocab if course has them
    if (behavior.hasVocab && page.sections.vocab) {
      count += page.sections.vocab.length;
    }

    // Count sentences if course has them
    if (behavior.hasSentences && page.sections.sentences) {
      count += page.sections.sentences.length;
    }
  });

  return count;
}

/**
 * Get item types for a course
 */
export function getCourseItemTypes(courseId) {
  return getCourseBehavior(courseId, 'itemTypes', ['vocab']);
}

/**
 * Get the label for sentences (some courses call them "lyrics")
 */
export function getSentenceLabel(courseId) {
  return getCourseBehavior(courseId, 'sentenceLabel', 'sentences');
}

/**
 * Get layout style for course QR page
 * 'single' = phonics-style (all items on one page)
 * 'tabbed' = southstreet-style (tabs for vocab/sentences)
 */
export function getCourseLayout(courseId) {
  return getCourseBehavior(courseId, 'layout', 'single');
}

/**
 * Get number of free lessons for a course
 */
export function getFreeLessons(courseId) {
  return getCourseBehavior(courseId, 'freeLessons', 0);
}

/**
 * Is this one of the course's free lessons — the try-before-you-buy a signed-in
 * non-owner may open (2026-10-12)? The first `freeLessons` lessons of the course's
 * first book, in config order, skipping 即将推出. Nothing else: a free lesson is
 * never a reason to open any other lesson.
 */
export async function isFreeLesson(courseId, book, lessonNumber) {
  const count = getFreeLessons(courseId);
  if (!count) return false;
  const lessons = (await getLessonsByCourse(courseId)) || [];
  if (!lessons.length) return false;
  const bookNum = (b) => parseInt(String(b).replace(/\D/g, ''), 10) || 0;
  const firstBook = lessons.reduce((min, l) => (bookNum(l.book) < bookNum(min) ? l.book : min), lessons[0].book);
  if (book !== firstBook) return false;
  const free = lessons.filter((l) => l.book === firstBook && !l.comingSoon).slice(0, count);
  const n = parseInt(lessonNumber, 10);
  return free.some((l) => parseInt(l.lessonNumber, 10) === n);
}

/**
 * Check if course has transcript tab (uses VTT subtitles)
 */
export function hasTranscript(courseId) {
  return getCourseBehavior(courseId, 'hasTranscript', false);
}

/**
 * Get transcript tab label for a course
 */
export function getTranscriptLabel(courseId) {
  return getCourseBehavior(courseId, 'transcriptLabel', '文本');
}

/**
 * Get sentence audio URL pattern for a course
 * Pattern uses {lessonNumber} and {sentenceIndex} placeholders
 */
export function getSentenceAudioPattern(courseId) {
  return getCourseBehavior(courseId, 'sentenceAudioPattern', null);
}

/**
 * Get lesson number padding for a course
 * Used to format lesson numbers in audio URLs (e.g., 3 for '001', 2 for '01')
 */
export function getLessonNumberPadding(courseId) {
  return getCourseBehavior(courseId, 'lessonNumberPadding', 0);
}

/**
 * Get vocab audio URL pattern for a course
 * Pattern uses {word} placeholder (lowercase, spaces replaced with underscores)
 */
export function getVocabAudioPattern(courseId) {
  return getCourseBehavior(courseId, 'vocabAudioPattern', null);
}

/**
 * Check if a course uses direct quiz mode (no crowns, no overview).
 */
export function isDirectQuizCourse(courseId) {
  return getCourseBehavior(courseId, 'quizMode', null) === 'direct';
}

/**
 * Get combined audio URL pattern for a course (used in combined/整段背诵 mode)
 * Pattern uses {lessonNumber} placeholder, returns full passage MP3 URL
 */
export function getCombinedAudioPattern(courseId) {
  return getCourseBehavior(courseId, 'combinedAudioPattern', null);
}

/**
 * Get theme color for course (used for tabs, buttons, active states)
 * Supports per-book colors via themeColorsByBook config
 * Returns default blue if not specified
 */
export function getCourseThemeColor(courseId, book = null) {
  const behavior = getCourseBehaviorConfig(courseId);
  // Check for per-book theme colors first
  if (book && behavior?.themeColorsByBook?.[book]) {
    return behavior.themeColorsByBook[book].color;
  }
  return behavior?.themeColor || '#3b82f6';
}

/**
 * Get theme gradient for course (used for header background)
 * Supports per-book gradients via themeColorsByBook config
 * Returns default purple gradient if not specified
 */
export function getCourseThemeGradient(courseId, book = null) {
  const defaultGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  const behavior = getCourseBehaviorConfig(courseId);
  // Check for per-book theme gradients first
  if (book && behavior?.themeColorsByBook?.[book]) {
    return behavior.themeColorsByBook[book].gradient;
  }
  return behavior?.themeGradient || defaultGradient;
}
